import { useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useIsDesktop } from '../../hooks/useIsDesktop.js';
import { useViewNavigate } from '../../hooks/useViewNavigate.js';
import { useConversationController } from './useConversationController.js';
import { ConversationMobile } from './ConversationMobile.js';
import { ConversationDesktop } from './ConversationDesktop.js';
import { useSfxRouter } from '../../lib/sfx/index.js';
import type { SceneRestartState } from './assisted/assistedModel.js';
import './conversation.css';

/**
 * Conversación responsive. La lógica (sesión de voz, settings, grabación,
 * inspire, drill) vive UNA sola vez en `useConversationController`; las dos
 * shells (`ConversationMobile` / `ConversationDesktop`) son presentacionales y
 * comparten la lista de mensajes (`ConversationMessages`) y todos los leaf.
 *
 * El screen envuelve la instancia real con un `key` (epoch): "Retomar la
 * charla" tras un cierre server-side (watch idle-no-speech) navega (replace)
 * con `resumeSessionId` de la sesión cerrada en `location.state` — preservando
 * el resto del contexto de arranque (escenario/examen/etc.) — y sube el epoch
 * → REMONTA la instancia entera, que entra por el flujo "Continuar": historial
 * sembrado + contexto LLM re-hidratado, SIN opener nuevo (habla el user).
 * Revivir la sesión in-place no es opción: la fila quedó finalizada
 * server-side y el estado local sería residual.
 *
 * El epoch viaja DENTRO del `location.state` de esa misma navegación. React
 * Router 7 aplica el cambio de location en un `startTransition`: un epoch en
 * `useState` aparte remontaba ANTES, con el state viejo, y la instancia nueva
 * congelaba el contexto de arranque anterior (`useStartContext` lo lee una vez)
 * — «Próxima» reabría la misma parte y «Retomar» abría una charla nueva
 * (medido en el encargo 104). Con el epoch en el state, key y contexto cambian
 * en el mismo render.
 */
/** Clave del epoch de remount dentro de `location.state`. */
const EPOCH_KEY = 'conversationEpoch';

function epochOf(state: unknown): number {
  const value = (state as Record<string, unknown> | null)?.[EPOCH_KEY];
  return typeof value === 'number' ? value : 0;
}

export function ConversationScreen() {
  const navigate = useViewNavigate();
  const location = useLocation();
  const epoch = epochOf(location.state);

  const resume = useCallback(
    (resumeSessionId: string | null): void => {
      const prev = (location.state ?? {}) as Record<string, unknown>;
      void navigate(location.pathname, {
        replace: true,
        state: { ...prev, ...(resumeSessionId ? { resumeSessionId } : {}), [EPOCH_KEY]: epoch + 1 },
        viewTransition: false,
      });
    },
    [navigate, location.pathname, location.state, epoch],
  );

  const nextLesson = (unitId: string, subUnitId: string) => {
    // Misma unidad: conserva su título para el encabezado («Programa · …»).
    const prev = (location.state ?? {}) as Record<string, unknown>;
    const unitTitle = prev['overrideUnitId'] === unitId ? prev['unitTitle'] : undefined;
    void navigate('/conversation', {
      replace: true,
      state: {
        fromProgram: true,
        overrideUnitId: unitId,
        overrideSubUnitId: subUnitId,
        ...(typeof unitTitle === 'string' ? { unitTitle } : {}),
        [EPOCH_KEY]: epoch + 1,
      },
      viewTransition: false,
    });
  };
  // «Repetir» un escenario: el mismo contexto de arranque, sesión nueva.
  const restart = () => {
    const { resumeSessionId: _resumed, ...rest } = (location.state ?? {}) as Record<string, unknown>;
    void navigate(location.pathname, { replace: true, state: { ...rest, [EPOCH_KEY]: epoch + 1 }, viewTransition: false });
  };
  // Charla asistida: «Probar solo» arranca la misma escena de nuevo, sin ayuda
  // (el coach empieza otra vez): el arranque de la escena en modo solo, sesión
  // nueva. Con el epoch en el state, la instancia nueva lo lee ya aplicado.
  const restartScene = (state: SceneRestartState) => {
    void navigate('/conversation', {
      replace: true,
      state: { ...state, [EPOCH_KEY]: epoch + 1 },
      viewTransition: false,
    });
  };
  return (
    <ConversationInstance
      key={epoch}
      onResume={resume}
      onNextLesson={nextLesson}
      onRestart={restart}
      onRestartScene={restartScene}
    />
  );
}

function ConversationInstance({
  onResume,
  onNextLesson,
  onRestart,
  onRestartScene,
}: {
  onResume: (resumeSessionId: string | null) => void;
  onNextLesson: (unitId: string, subUnitId: string) => void;
  onRestart: () => void;
  onRestartScene: (state: SceneRestartState) => void;
}) {
  const isDesktop = useIsDesktop();
  const ctrl = useConversationController({ onResume, onNextLesson, onRestart, onRestartScene });
  // Eventos de ciclo de sesión (cierre, preaviso idle) → SFX. Montado acá una
  // sola vez, no en cada shell. Las celebraciones van por <CelebrationLayer sound>.
  useSfxRouter();
  return isDesktop ? <ConversationDesktop ctrl={ctrl} /> : <ConversationMobile ctrl={ctrl} />;
}
